import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { TimeSlotEntry } from '../types/booking';
import { ApiData } from '../types/rest';

interface UseAvailabilitiesProps {
	bookingBusinessId: string;
	bookingServiceId: string;
	data: ApiData;
	timezone: string;
}

export const useAvailabilities = ({ bookingBusinessId, bookingServiceId, data, timezone }: UseAvailabilitiesProps) => {
	const [availabilities, setAvailabilities] = useState<TimeSlotEntry[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const requestStaffAvailabilityTimeSlots = useCallback(async (): Promise<TimeSlotEntry[] | null> => {
		try {
			const response = await axios.post(data.rest_routes.staff_availability, {
				booking_business_id: bookingBusinessId,
				booking_service_id: bookingServiceId,
				timezone: timezone,
			});

			return response.data || null;
		} catch (err) {
			console.error('Error fetching time slots:', err);
			throw err; // Throw error instead of returning empty array
		}
	}, [bookingBusinessId, bookingServiceId, data.rest_routes.staff_availability, timezone]);

	const fetchAvailabilities = useCallback(
		async (callback?: (timeSlots: TimeSlotEntry[]) => void) => {
			setIsLoading(true);

			try {
				const timeSlots = await requestStaffAvailabilityTimeSlots();

				// Only update if we got valid data
				if (timeSlots !== null) {
					setAvailabilities(timeSlots);
					if (callback) {
						callback(timeSlots); // Call the callback with the fetched time slots
					}
				} else {
					console.warn('No time slots available');
				}
			} catch (error) {
				console.error('Error fetching availabilities:', error);
				// Don't clear availabilities on error - keep existing data
			} finally {
				setIsLoading(false);
			}
		},
		[requestStaffAvailabilityTimeSlots],
	);

	useEffect(() => {
		fetchAvailabilities();
	}, [fetchAvailabilities]);

	return {
		availabilities,
		isLoading,
		fetchAvailabilities,
	};
};
